import { ShieldCheck } from "lucide-react";
import { formatCurrency } from "../lib/api";
import { CONFIDENCE_DISCLOSURE, formatAsOf } from "../lib/heroBalance";

/**
 * Discloses where a partner-held balance came from, and when.
 *
 * STARSEED-CAPMARKETS-115.
 *
 * Two deliberate choices worth keeping:
 *
 * 1. **`gold`, not `warning`.** This is provenance, not an alarm. Styling a
 *    permanent disclosure as a warning is how CAPMARKETS-110 trained a
 *    reviewer to disregard a channel — a signal that fires on every load
 *    stops being read. Gold is the repo's trust-bearing colour, and the
 *    disclosure should read as *this is where the number is from*, not
 *    *something is wrong*.
 * 2. **The date shown is the STALEST** across the member's accounts, computed
 *    server-side. Showing the newest would let one freshly-imported account
 *    make a month-old total look current.
 *
 * Renders nothing when there is no partner-held capital — a member with no
 * statement gets no figure and no disclosure, which is different from a
 * balance of zero.
 */
const PartnerBalanceDisclosure = ({ partner }) => {
  if (!partner) return null;

  const disclosure = CONFIDENCE_DISCLOSURE[partner.confidence] || null;
  const asOf = formatAsOf(partner.as_of);
  const accounts = partner.accounts_count;

  // CAPMARKETS-287. The server decides what stale means; this only renders it.
  // Read defensively so a payload from before this shipped renders exactly as
  // it did then, rather than throwing on a member's balance card.
  const freshness = partner.freshness || null;
  const ageDays = freshness ? freshness.age_days : null;
  // BOTH signals, because the server exposes them as independent and the
  // comparative one is the stronger. A member two days old while the platform
  // has already moved on is exactly the case this shipped for, and checking
  // only `is_stale` would have shown them nothing. (Copilot, PR #2630.)
  const isStale = Boolean(freshness && freshness.is_stale);
  const isBehind = Boolean(freshness && freshness.is_behind_cohort);
  // A flagged figure is always at least a day old, so this never reads "0
  // days". The server counts both signals in UTC calendar days: stale needs more
  // than the threshold, and behind needs a statement DATED earlier than the
  // cohort's. `test_a_flagged_figure_is_always_at_least_a_day_old` pins that.
  // For one review round this had a same-day line for "behind at age 0". Once
  // the server stopped comparing times of day, that line could no longer be
  // reached, and before that it could only render when it was likely false.
  // (/code-review, PR #2630.)
  const showAge = (isStale || isBehind) && Number.isFinite(ageDays);

  // Absent is not zero — the same rule the rest of this surface follows.
  const amount =
    partner.balance === null || partner.balance === undefined
      ? "Unavailable"
      : formatCurrency(partner.balance);

  return (
    <div
      className="mt-4 rounded-lg border border-gold/30 bg-gold/5 p-4"
      data-testid="partner-balance-disclosure"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 shrink-0 rounded-md bg-gold/10 flex items-center justify-center">
          <ShieldCheck className="text-gold" size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="text-xs uppercase tracking-wide text-gold">
              Partner-held
            </p>
            <p
              className="text-sm font-semibold tabular-nums"
              data-testid="partner-balance-amount"
            >
              {amount}
            </p>
            {accounts > 1 && (
              <span
                className="text-xs text-muted-foreground"
                data-testid="partner-accounts-count"
              >
                across {accounts} accounts
              </span>
            )}
          </div>

          {/* Provenance. Rendered as text rather than a tooltip so the member
              does not have to hunt for it (AC1). */}
          <p className="mt-1 text-xs text-muted-foreground">
            {asOf ? (
              <>
                As of{" "}
                <span data-testid="partner-as-of" className="font-medium">
                  {asOf}
                </span>
              </>
            ) : (
              <span data-testid="partner-as-of-unknown">
                Statement date unavailable
              </span>
            )}
            {disclosure && (
              <>
                {" · "}
                <span data-testid="partner-confidence">{disclosure.label}</span>
              </>
            )}
            {/* In the existing provenance line rather than a banner. A member
                whose figure is a fortnight old needs the date qualified, not a
                warning shouted over the number — and the same line already
                carries "as of" and how the figure was obtained, so age belongs
                beside them. Nothing renders when the figure is current. */}
            {showAge && (
              <>
                {" · "}
                <span
                  data-testid="partner-staleness"
                  className="font-medium text-burgundy"
                >
                  not updated in {ageDays} {ageDays === 1 ? "day" : "days"}
                </span>
              </>
            )}
          </p>

          {disclosure && (
            <p className="mt-1 text-xs text-muted-foreground">
              {disclosure.detail} Held by our partner, separately from the
              Native Fund.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerBalanceDisclosure;
